import {
  IconButton,
  Input,
  Stack,
  Text,
  VStack,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import {
  KYDAdminEvent,
  KYDAdminEventSummary,
} from "@common/interfaces/KYDEvent";
import { KYDAdminFeedItem } from "@common/interfaces/KYDOrganization";
import dayjs from "dayjs";
import { MdCancel } from "react-icons/md";
import { formatDate } from "../../../utils";
import { useSearchBox, useInstantSearch } from "react-instantsearch";
import InfiniteScroll from "../../Events/InfiniteScroll";
import { useMemo } from "react";
import EventCard from "./EventCard";

const EventsTable = ({
  external = [],
  selected = [],
  onAdd,
  onRemove,
  events,
  isLastPage,
  showMore,
}: {
  onAdd: (
    event: KYDAdminEventSummary | KYDAdminFeedItem,
    type: "event" | "external_event"
  ) => Promise<void>;
  onRemove: (
    event: KYDAdminEventSummary | KYDAdminFeedItem,
    type: "event" | "external_event"
  ) => Promise<void>;
  isLoading?: boolean;
  external: KYDAdminFeedItem[];
  events: KYDAdminEventSummary[];
  selected: Array<KYDAdminEvent["id"] | KYDAdminFeedItem["id"]>;
  isLastPage: boolean;
  showMore: () => void;
}) => {
  const { status: searchStatus } = useInstantSearch();
  const { refine, query } = useSearchBox();
  const filteredExternalEvents = useMemo(() => {
    return external.filter((e) =>
      e.title.toLowerCase().includes(query.toLowerCase())
    );
  }, [external, query]);

  return (
    <VStack w="full">
      <InputGroup>
        <Input
          placeholder="Search for events or venues..."
          value={query}
          onChange={(e) => refine(e.target.value)}
        />
        <InputRightElement>
          {query ? (
            <IconButton
              isLoading={searchStatus === "loading"}
              isDisabled={query === ""}
              variant={"ghost"}
              background="transparent"
              aria-label="Cancel"
              icon={<MdCancel />}
              onClick={() => refine("")}
            />
          ) : null}
        </InputRightElement>
      </InputGroup>

      <Stack
        direction={["column", "row"]}
        flexBasis={["100%", "50%"]}
        w="full"
        spacing={4}
      >
        <VStack w="100%" maxH="50vh" overflowY={"scroll"} p={1}>
          <Text fontWeight={"bold"} w="full">
            Events{" "}
          </Text>
          {events.length === 0 && query ? (
            <Text fontSize={"small"} w="full" py={2}>
              No KYD events found matching "{query}"
            </Text>
          ) : null}

          {events.map((e) => {
            return (
              <EventCard
                key={e.id}
                name={e.name}
                address={e.venue?.address}
                link={""}
                image={e.image}
                isSelected={selected.includes(e.id)}
                onAdd={() => onAdd(e, "event")}
                onRemove={() => onRemove(e, "event")}
                startAt={e.display_start_at}
                venueName={e.venue?.name}
                status={e.status}
              />
            );
          })}
          <InfiniteScroll
            hasMore={!isLastPage}
            loadMore={showMore}
            threshold={300} // Load more items when user is 300px from bottom
            preloadBatch={true} // Add this to preload the next batch
          />
        </VStack>
        <VStack w="100%" maxH="50vh" overflowY={"scroll"} p={1}>
          <Text fontWeight={"bold"} w="full">
            External events
          </Text>
          {filteredExternalEvents.length === 0 && query ? (
            <Text fontSize={"small"} w="full" py={2}>
              No external events found matching "{query}"
            </Text>
          ) : null}

          {filteredExternalEvents.map((e) => (
            <EventCard
              key={e.id}
              name={e.title}
              address={""}
              image={e.image}
              link={e.link}
              isSelected={selected.includes(e.id)}
              onAdd={() => onAdd(e, "external_event")}
              onRemove={() => onRemove(e, "external_event")}
              startAt={formatDate(dayjs(e.start_at))}
              venueName={e.venue_name}
            />
          ))}
          {external.length === 0 && !query ? (
            <Text fontSize={"small"} w="full" py={2}>
              No external events. You can add them in the "Bio" section
            </Text>
          ) : null}
        </VStack>
      </Stack>
    </VStack>
  );
};

export default EventsTable;
